import { groupAttendees } from '../../lib/calendar/attendeeStatus';

const initials = (p) => {
    const src = (p.name || p.email || '?').trim();
    const parts = src.includes('@') ? [src[0]] : src.split(/\s+/).map(w => w[0]);
    return parts.slice(0, 2).join('').toUpperCase();
};

/**
 * The full event view's right panel (#680, prototype "Tracking"): who is
 * coming, grouped by response. Built from the attendee statuses the providers
 * already return (normalised in the aggregator); nothing is written back — the
 * prototype's RSVP "Change" is out of scope.
 */
export function TrackingPanel({ event }) {
    const { groups, hasAnyStatus, summary } = groupAttendees(event.attendees);
    return (
        <aside className="w-full lg:w-[300px] flex-none box-border bg-[#FBFAF9] lg:border-l border-t lg:border-t-0 border-[#E9E7E2] px-[18px] pt-[18px] pb-10 overflow-y-auto" aria-label="Attendee responses" data-testid="event-tracking-panel">
            <div className="flex items-center gap-2 mb-3.5">
                <h2 className="flex-1 text-[13.5px] font-semibold text-[#0C0A09]">Tracking</h2>
                {summary && <span className="text-[11.5px] text-[#8A837E]">{summary}</span>}
            </div>
            {event.organizer_email && (
                <>
                    <div className="text-[10.5px] font-semibold tracking-[0.09em] uppercase text-[#A29B95] mb-2">Organizer</div>
                    <div className="flex items-center gap-2.5 mb-[18px] min-w-0">
                        <span className="flex-none w-[30px] h-[30px] rounded-full bg-[#EFEAFE] text-[#3B0DBE] flex items-center justify-center text-xs font-semibold">
                            {initials({ email: event.organizer_email })}
                        </span>
                        <span className="text-[13px] font-medium text-[#0C0A09] truncate">{event.organizer_email}</span>
                    </div>
                </>
            )}
            {groups.length === 0 && <p className="text-[12.5px] text-[#8A837E]">No attendees on this event.</p>}
            {groups.length > 0 && !hasAnyStatus && (
                <p className="text-[12px] text-[#8A837E] mb-3">Response status isn't available for this event.</p>
            )}
            {hasAnyStatus && groups.map(g => (
                <div key={g.key} className="mb-4">
                    <div className="flex items-center gap-[7px] mb-2">
                        <span className="w-2 h-2 rounded-full" style={{ background: g.color }} aria-hidden="true" />
                        <span className="text-[12px] font-semibold text-[#3A3532]">{g.label}</span>
                        <span className="text-[11.5px] text-[#A29B95]">{g.people.length}</span>
                    </div>
                    {g.people.map(p => (
                        <div key={p.email || p.name} className="flex items-center gap-2.5 py-[5px] min-w-0">
                            <span className="flex-none w-[26px] h-[26px] rounded-full bg-[#F1EFEA] text-[#57504B] flex items-center justify-center text-[11px] font-semibold">{initials(p)}</span>
                            <div className="min-w-0">
                                <div className="text-[12.5px] text-[#0C0A09] truncate">{p.name || p.email}</div>
                                {/* Role (prototype), from the provider's optional flag. */}
                                <div className="text-[11px] text-[#A29B95] truncate" data-testid="attendee-role">
                                    {[p.optional ? 'Optional' : 'Required', p.name && p.email ? p.email : null].filter(Boolean).join(' · ')}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ))}
            {!hasAnyStatus && groups.flatMap(g => g.people).map(p => (
                <div key={p.email || p.name} className="text-[12.5px] text-[#3A3532] py-1 truncate">{p.name || p.email}</div>
            ))}
        </aside>
    );
}
